import * as React from 'react';
import useAutocomplete from '@mui/material/useAutocomplete';
import { cva } from 'class-variance-authority';
import { IonicInput } from 'ionic-ui';

/*---TYPE DEFINITIONS---*/
export interface OptionsType {
  id: any;
  icon?: React.ReactNode;
  name: string;
}

export interface AutoCompleteInputProps {
  /** Async fetcher: receives the current input value, returns array of options */
  fetchOptions: (query: string) => Promise<OptionsType[]>;
  /** Called when user selects an option (returns the selected option object or null if cleared). */
  onChange?: (value: OptionsType | null) => void;
  /** The currently selected option object. */
  value?: OptionsType | null;
  /** Optional UI props */
  label?: string;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  /** Debounce delay for backend calls (ms) */
  debounceTime?: number;
  hintMsg?: string;
  disabled?: boolean;
}

type Props = AutoCompleteInputProps & React.ComponentProps<typeof IonicInput>;

const menuVariants = cva(
  'w-full rounded-md z-10 sm:min-w-32 max-h-[40vh] absolute shadow-md border overflow-auto dashboard-scroller',
  {
    variants: {
      variant: {
        default: 'bg-background dark:bg-background-accent',
        filled: 'bg-[#F2F4F7] dark:bg-background-accent',
      },
      size: {
        xs: 'text-xs',
        sm: 'text-sm',
        default: 'text-sm',
        lg: 'text-base',
        xlg: 'text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

/*---COMPONENT---*/
export function AutoCompleteInput({
  fetchOptions,
  onChange,
  label = 'Select option',
  placeholder = 'Type to search…',
  containerClassName = '',
  debounceTime = 300,
  hintMsg,
  value: rootValue,
  disabled,
  ...props
}: Props) {
  const [options, setOptions] = React.useState<OptionsType[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  // Keep the typed query separate from the selected option label.
  React.useEffect(() => {
    setInputValue(rootValue?.name ?? '');
  }, [rootValue?.id, rootValue?.name]);

  /** Debounced fetch on input change */
  React.useEffect(() => {
    if (!fetchOptions) return;
    const handler = setTimeout(async () => {
      try {
        const data = await fetchOptions(inputValue);
        setOptions(data);
      } catch (e) {
        console.error('Autocomplete fetch failed:', e);
        setOptions([]);
      }
    }, debounceTime);
    return () => clearTimeout(handler);
  }, [inputValue, fetchOptions, debounceTime]);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete<OptionsType>({
    options: options || [],
    getOptionLabel: (opt) => opt.name || '',
    onInputChange: (_, value) => {
      setInputValue(value);
      if (value === '') onChange?.(null);
    },
    onChange: (_, value) => onChange?.(value ? value : null),
    disabled,
    value: rootValue || null,
    inputValue: inputValue,
  });

  return (
    <div>
      <div className={`relative ${containerClassName}`}>
        <div {...getRootProps()}>
          {label && (
            <label
              {...getInputLabelProps()}
              className="block mb-1 text-sm font-medium medium-emphasis-text"
            >
              {label}
            </label>
          )}
          <IonicInput
            {...getInputProps()}
            placeholder={placeholder}
            {...props}
          />
        </div>

        {groupedOptions.length > 0 && (
          <ul
            {...getListboxProps()}
            className={menuVariants({
              variant: props.variant as any,
              size: props.size as any,
            })}
          >
            {groupedOptions.map((option, index) => {
              const { key, ...optionProps } = getOptionProps({ option, index });
              return (
                <li
                  key={key}
                  {...optionProps}
                  className="
                  cursor-pointer px-3 high-emphasis-text py-2 hover:bg-muted max-w-full truncate
                  text-sm space-x-2 high-emphasis-text
                  aria-selected:bg-muted
                "
                >
                  {option.icon && <span>{option.icon}</span>}
                  <span>{option.name}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {hintMsg && (
        <label className="text-xs text-red-600 mt-1">{hintMsg}</label>
      )}
    </div>
  );
}
